import CloseIcon from '@mui/icons-material/Close';
import { Box, Chip } from '@mui/material';
import { STATES_MAP } from '@repo/supergraph/marketplace/constants';
import { useFilterEvents } from '@repo/ui/OffersFilter/useFilterEvents';
import { startTransition, useCallback, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/shallow';
import { useOffersFilterStore } from '../store';
import { OffersFilterStoreState, sortByDefaultValue } from '../store/store';

export type SelectedFilterPillsProps = {
  isMobile: boolean;
  analyticsProps: {
    actionSection: string;
    pageIdentifier: string;
  };
  onClearFilters?: () => void;
  regionCode?: string;
  isBankBonusesFilterExperiment?: boolean;
};

export const SelectedFilterPills = ({
  isMobile = false,
  analyticsProps,
  onClearFilters,
  regionCode,
  isBankBonusesFilterExperiment,
}: SelectedFilterPillsProps) => {
  const {
    appliedFilters,
    handleUpdateFilterFormState,
    handleApplyFilters,
    handleSortBy,
    sortBy,
    sortOptions,
    handleClearFilters,
  } = useOffersFilterStore(
    useShallow((state: OffersFilterStoreState) => ({
      appliedFilters: state.appliedFilters,
      handleUpdateFilterFormState: state.handleUpdateFilterFormState,
      handleApplyFilters: state.handleApplyFilters,
      handleSortBy: state.handleSortBy,
      sortBy: state.sortBy,
      sortOptions: state.sortOptions,
      handleClearFilters: state.handleClearFilters,
    })),
  );

  const updateTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const { clearAllFiltersEvent, removeFilterEvent } = useFilterEvents({
    actionSection: analyticsProps.actionSection,
    pageIdentifier: analyticsProps.pageIdentifier,
    additionalAttributes: {},
  });

  const effectiveOnClearFilters = onClearFilters || handleClearFilters;

  const onClearFiltersHandler = useCallback(() => {
    if (updateTimeoutRef.current) {
      clearTimeout(updateTimeoutRef.current);
    }

    updateTimeoutRef.current = setTimeout(() => {
      startTransition(() => {
        effectiveOnClearFilters();
        clearAllFiltersEvent();
      });
    }, 100);
  }, [effectiveOnClearFilters, clearAllFiltersEvent]);

  const handleRemoveFilter = useCallback(
    (selectedLabel: string) => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }

      const isProductType = appliedFilters?.selectedProductTypes.includes(selectedLabel);
      const isTermLength = appliedFilters?.selectedTermRanges.includes(selectedLabel);
      const isMinDeposit = appliedFilters?.selectedMinDepositRanges.includes(selectedLabel);
      const isMinBalance = appliedFilters?.selectedMinBalanceRanges.includes(selectedLabel);
      const isBonus = appliedFilters?.selectedBonusOptions.includes(selectedLabel);
      const isSortBy = sortOptions.find(sortByValue => sortByValue.label === selectedLabel);

      updateTimeoutRef.current = setTimeout(() => {
        startTransition(() => {
          if (isProductType) {
            handleUpdateFilterFormState({
              selectedProductTypes:
                appliedFilters?.selectedProductTypes.filter(label => label !== selectedLabel) || [],
            });
          } else if (isTermLength) {
            handleUpdateFilterFormState({
              selectedTermRanges:
                appliedFilters?.selectedTermRanges.filter(label => label !== selectedLabel) || [],
            });
          } else if (isMinDeposit) {
            handleUpdateFilterFormState({
              selectedMinDepositRanges:
                appliedFilters?.selectedMinDepositRanges.filter(label => label !== selectedLabel) ||
                [],
            });
          } else if (isMinBalance) {
            handleUpdateFilterFormState({
              selectedMinBalanceRanges:
                appliedFilters?.selectedMinBalanceRanges.filter(label => label !== selectedLabel) ||
                [],
            });
          } else if (isBonus) {
            handleUpdateFilterFormState({
              selectedBonusOptions:
                appliedFilters?.selectedBonusOptions.filter(label => label !== selectedLabel) || [],
            });
          } else if (isSortBy) {
            handleSortBy(sortByDefaultValue);
          }
          handleApplyFilters();
          removeFilterEvent({
            additional_attributes: {
              filter_cleared: selectedLabel,
            },
          });
        });
      }, 100);
    },
    [
      appliedFilters,
      handleUpdateFilterFormState,
      handleApplyFilters,
      handleSortBy,
      sortByDefaultValue,
      removeFilterEvent,
      sortOptions,
    ],
  );

  const { allSelectedFilters, filterPillsToDisplay } = useMemo(() => {
    const filtersApplied = [
      ...(appliedFilters?.selectedProductTypes || []),
      ...(appliedFilters?.selectedTermRanges || []),
      ...(appliedFilters?.selectedMinDepositRanges || []),
      ...(appliedFilters?.selectedMinBalanceRanges || []),
      ...(appliedFilters?.selectedBonusOptions || []),
    ];

    if (isMobile || isBankBonusesFilterExperiment) {
      const sortByLabel = sortOptions.find(sortByValue => sortByValue.value === sortBy)?.label;
      if (sortByLabel) {
        filtersApplied.push(sortByLabel);
      }
    }
    if (isMobile || isBankBonusesFilterExperiment) {
      const location = appliedFilters?.selectedLocation ?? regionCode;
      const locationLabel = STATES_MAP[location as keyof typeof STATES_MAP];
      if (locationLabel) {
        filtersApplied.push(locationLabel);
      }
    }

    return {
      allSelectedFilters: filtersApplied,
      filterPillsToDisplay: filtersApplied.length,
    };
  }, [
    appliedFilters?.selectedProductTypes,
    appliedFilters?.selectedLocation,
    appliedFilters?.selectedTermRanges,
    appliedFilters?.selectedMinDepositRanges,
    appliedFilters?.selectedMinBalanceRanges,
    appliedFilters?.selectedBonusOptions,
    sortBy,
    isMobile,
    isBankBonusesFilterExperiment,
    regionCode,
    sortOptions,
  ]);

  const displayClearAll = filterPillsToDisplay >= 2 && !isMobile && !isBankBonusesFilterExperiment;

  if (isBankBonusesFilterExperiment && !filterPillsToDisplay) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        mt: isBankBonusesFilterExperiment ? 0 : 2,
        justifyContent: { md: 'space-between' },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          rowGap: 2,
          columnGap: isMobile ? 2 : 3,
          flexWrap: 'wrap',
        }}
      >
        {allSelectedFilters.map((filter, index) => {
          const location = appliedFilters?.selectedLocation ?? regionCode;
          const isLocationFilter =
            location && STATES_MAP[location as keyof typeof STATES_MAP] === filter;

          return (
            <Chip
              key={`${filter}-${index}`}
              label={filter}
              variant="filterPills"
              deleteIcon={isLocationFilter ? undefined : <CloseIcon />}
              onClick={isLocationFilter ? undefined : () => handleRemoveFilter(filter)}
              onDelete={isLocationFilter ? undefined : () => handleRemoveFilter(filter)}
              sx={{
                fontSize: { xs: 12, sm: 14 },
                cursor: isLocationFilter ? 'default' : 'pointer',
                ...(isLocationFilter &&
                  isBankBonusesFilterExperiment &&
                  !isMobile && {
                    position: 'relative',
                    overflow: 'visible',
                    '&::after': {
                      content: '"A state must be selected"',
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      backgroundColor: 'neutral.dark',
                      color: 'common.white',
                      py: 1,
                      px: 2,
                      borderRadius: '4px',
                      fontSize: '10px',
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                      zIndex: 9999,
                      opacity: 0,
                      transition: 'opacity 0.1s ease 0.2s',
                    },
                    '&:hover::after': {
                      opacity: 1,
                    },
                  }),
              }}
            />
          );
        })}
        {displayClearAll && (
          <Chip
            label="Clear All"
            variant="filterPills"
            onClick={onClearFiltersHandler}
            sx={{
              fontSize: { xs: 12, sm: 14 },
            }}
          />
        )}
      </Box>
    </Box>
  );
};
